import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlans, PURCHASED, NOT_PURCHASED } from '../utils/usePlans.js';
import { useUser } from '../hooks/useUser.js';
import { useUserPayments } from '../hooks/useUserPayments.js';
import {
  cancelMembership,
  registerPayment,
  getPortalSession,
} from '../api/user';
import { getSessionId, findOriginalPlan } from '../api/user';
import { logout } from '../api/auth';
import { checkCampaignCode, checkCampaignActive } from '../api/campaign';
import './MyPage.css';
import {
  getCompanyId,
  setCompanyId,
  getCampaignCode,
  setCampaignCode,
} from '../utils/storage.js';
import { formatDateShort } from '../utils/dateFormat.js';
import UserPageLayout from '../components/UserPageLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { NoticeModal, ConfirmModal } from '../components/Modals';

const MyPage = () => {
  const navigate = useNavigate();
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeMsg, setNoticeMsg] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState('');
  const [onConfirmAction, setOnConfirmAction] = useState(() => () => {});

  const showNotice = (msg) => {
    setNoticeMsg(msg);
    setNoticeOpen(true);
  };

  const showConfirm = (msg, onConfirm) => {
    setConfirmMsg(msg);
    setOnConfirmAction(() => onConfirm);
    setConfirmOpen(true);
  };

  const {
    oneTimeOptions,
    subscriptionOptions,
    loading: plansLoading,
    getPlanName,
  } = usePlans();
  const allPrices = useMemo(
    () => [...oneTimeOptions, ...subscriptionOptions],
    [oneTimeOptions, subscriptionOptions]
  );

  // SWRフックを使用してユーザー情報と支払い情報を取得
  const { user, isLoading: userLoading } = useUser();
  const {
    payments,
    isLoading: paymentsLoading,
    mutate: mutatePayments,
  } = useUserPayments();

  const companyId = getCompanyId();
  const campaignCode = getCampaignCode();
  const [trialChecked, setTrialChecked] = useState(false);
  const [campaignStatuses, setCampaignStatuses] = useState({});

  const registerPaymentIfNeed = useCallback(
    async (price_id) => {
      if (!companyId || !price_id) return;

      const plan = allPrices.find((p) => p.price_id === price_id);
      const exclusive_plan = plan?.exclusive_plan;
      if (exclusive_plan) {
        // キャンペーンコードの存在チェック
        const checkResult = await checkCampaignCode(exclusive_plan);

        // すでに登録済みの場合に中断
        if (checkResult.exists) {
          showNotice(
            'このdアカウントではすでにキャンペーン特典を利用済みのためお申し込みいただけません'
          );
          return;
        }
      }

      // ここから下は今までどおり
      const alreadyRegistered = payments.find((p) => {
        // price_idに対応するプランを探す
        const plan = allPrices.find((pl) => pl.price_id === p.price_id);

        return (
          p.price_id === price_id &&
          p.is_member === PURCHASED &&
          plan?.interval === 'month'
        );
      });
      if (alreadyRegistered) {
        return;
      }

      try {
        await registerPayment(companyId, price_id);
        mutatePayments();
        setCompanyId(null);
        setCampaignCode(null);
      } catch (err) {
        if (err.response && err.response.status === 400) {
          setCompanyId(null);
          setCampaignCode(null);
        } else {
          console.error('Payment registration failed:', err);
        }
      }
    },
    [companyId, payments, allPrices, mutatePayments]
  );

  const checkTrialPopup = useCallback(
    async (userPayments) => {
      if (!userPayments || userPayments.length === 0 || campaignCode === '')
        return;

      const trialPayment = userPayments.find(
        (p) => p.trial_ends_at !== null && p.price_id === campaignCode
      );

      if (trialPayment) {
        showNotice('こちらのプランはすでに利用済みです。');
      } else {
        try {
          await registerPaymentIfNeed(campaignCode);
        } catch (err) {
          console.error('無料プラン登録に失敗しました', err);
        }
      }
    },
    [campaignCode, registerPaymentIfNeed]
  );

  const getIsSubscription = useCallback(
    (price_id) => {
      return subscriptionOptions.some((option) => option.price_id === price_id);
    },
    [subscriptionOptions]
  );

  const getAmount = useCallback(
    (price_id) => {
      const price = allPrices.find((p) => p.price_id === price_id);
      return price ? price.amount : 0;
    },
    [allPrices]
  );

  const getTrialDays = (price_id) => {
    const price = allPrices.find((p) => p.price_id === price_id);
    return price ? price.trial_days : 0;
  };

  const checkCampaignStatus = useCallback(async (price_id) => {
    try {
      const result = await checkCampaignActive(price_id);
      setCampaignStatuses((prev) => ({
        ...prev,
        [price_id]: result.is_active === 1,
      }));
    } catch (error) {
      console.error('キャンペーン有効性チェックエラー:', error);
      setCampaignStatuses((prev) => ({
        ...prev,
        [price_id]: false,
      }));
    }
  }, []);

  const [originalPlans, setOriginalPlans] = useState({});
  useEffect(() => {
    const fetchOriginalPlans = async () => {
      if (!payments.length) return;

      const newPlans = {};
      for (const p of payments) {
        try {
          const res = await findOriginalPlan(p.price_id);
          newPlans[p.price_id] = res?.original_plan || null;
        } catch (err) {
          console.error('findOriginalPlan error:', err);
          newPlans[p.price_id] = null;
        }
      }

      setOriginalPlans(newPlans);
    };

    fetchOriginalPlans();
  }, [payments]);

  const getPaymentMember = (isMember, price_id) => {
    const isSubscription = getIsSubscription(price_id);
    const isCampaignActive = campaignStatuses[price_id] !== false;

    // キャンペーンが無効な場合は常に「無効」を表示
    if (!isCampaignActive) return '無効';
    if (isMember === NOT_PURCHASED) return '契約前';
    if (isMember === PURCHASED) {
      return isSubscription ? '契約中' : '購入済み';
    }
    return '解約済み';
  };

  const handleStartMembership = (companyId, price_id, plan_name) => {
    showConfirm(
      `「${plan_name}」プランを契約します。\n契約を続行してもよろしいですか？`,
      () => {
        navigate('/terms', { state: { userId: user.id, companyId, price_id } });
      }
    );
  };

  const handleCancelMembership = async (subscriptionId, plan_name) => {
    showConfirm(
      `「${plan_name}」プランの契約を解約します。\n解約すると次回更新日以降は利用できません。\n本当に解約しますか？`,
      async () => {
        try {
          const sessionRes = await getSessionId(subscriptionId);
          const sessionId = sessionRes.checkout_session_id;
          await cancelMembership(subscriptionId);
          showNotice('解約が完了しました');
          mutatePayments();
          navigate(`/complete?session_id=${sessionId}`, {
            state: { status: 'cancel' },
          });
        } catch (err) {
          showNotice('解約処理に失敗しました');
        }
      }
    );
  };

  const getPaymentButtons = (
    isMember,
    campaignCode,
    linkageIdentifier,
    companyId,
    price_id,
    plan_name,
    isSubscription
  ) => {
    let buttonText = '';
    let buttonColor = '';
    let buttonVariant = '';
    if (isMember === NOT_PURCHASED) {
      buttonText = '契約手続きに進む';
      buttonColor = 'info';
      buttonVariant = 'outlined';
    } else if (isMember === PURCHASED) {
      buttonText = 'シリアルコード発行に進む';
      buttonColor = 'info';
      buttonVariant = 'outlined';
    } else {
      buttonText = 'シリアルコード停止に進む';
      buttonColor = 'error';
      buttonVariant = 'outlined';
    }

    const handleButtonClick = async () => {
      if (isMember === NOT_PURCHASED) {
        try {
          const res = await findOriginalPlan(price_id);

          const planName = res.plan_name;
          const linkageIdentifier = res.linkage_identifier;
          const originalPlan = res.original_plan;

          if (
            !planName ||
            originalPlan?.plan?.upgrade_type === 1 ||
            originalPlan?.is_member !== 1
          ) {
            await handleStartMembership(companyId, price_id, plan_name);
            return;
          }

          showConfirm(
            `「${planName}」が解約されますがよろしいですか？`,
            async () => {
              await handleStartMembership(companyId, price_id, plan_name);
            }
          );
        } catch (error) {
          console.error('元プラン検索または解約処理でエラー', error);
        }
      } else {
        // POSTでdocomo外部システムに送るためフォーム作成
        const baseUrl = process.env.REACT_APP_REDIRECT_URL;

        const form = document.createElement('form');
        form.method = 'POST';

        form.action = baseUrl;

        const campaignInput = document.createElement('input');
        campaignInput.type = 'hidden';
        campaignInput.name = 'campaign_code';
        campaignInput.value = campaignCode || '';

        const linkageInput = document.createElement('input');
        linkageInput.type = 'hidden';
        linkageInput.name = 'linkage_identifier';
        linkageInput.value = linkageIdentifier || '';

        form.appendChild(campaignInput);
        form.appendChild(linkageInput);
        document.body.appendChild(form);
        form.submit();
      }
    };

    // キャンペーンが無効で契約前の場合はボタンを非表示
    const originalPlan = originalPlans[price_id] || null;
    const isCampaignActive = campaignStatuses[price_id] !== false;
    const shouldShowButton =
      (isCampaignActive &&
        (originalPlan === null || originalPlan?.is_member !== 0)) ||
      buttonText === 'シリアルコード停止に進む';

    return (
      <>
        {shouldShowButton && (
          <Button
            onClick={handleButtonClick}
            variant={buttonVariant}
            size="small"
            color={buttonColor}
          >
            {buttonText}
          </Button>
        )}
        {isMember === PURCHASED && isSubscription && (
          <Button
            onClick={() => handleCancelMembership(linkageIdentifier, plan_name)}
            variant="outlined"
            size="small"
            color="error"
          >
            解約手続きに進む
          </Button>
        )}
        {isMember === PURCHASED && isSubscription && isCampaignActive && (
          <Button
            onClick={() => handleCardInfoChange(linkageIdentifier)}
            variant="outlined"
            size="small"
            color="success"
          >
            カード情報変更
          </Button>
        )}
      </>
    );
  };

  useEffect(() => {
    if (plansLoading || paymentsLoading) return;
    if (payments.length > 0 && !trialChecked) {
      setTrialChecked(true);
      setTimeout(() => {
        checkTrialPopup(payments);
      }, 200);
    }
  }, [payments, checkTrialPopup, plansLoading, paymentsLoading, trialChecked]);

  useEffect(() => {
    if (plansLoading || paymentsLoading) return;

    // 支払い情報が空の場合のみregisterPaymentIfNeedを実行
    if (payments.length === 0 && campaignCode) {
      registerPaymentIfNeed(campaignCode);
    }
  }, [
    navigate,
    plansLoading,
    paymentsLoading,
    campaignCode,
    payments.length,
    registerPaymentIfNeed,
    mutatePayments,
  ]);

  // キャンペーン有効性をチェック
  useEffect(() => {
    if (!payments.length) return;

    // 各プランのキャンペーン有効性をチェック
    payments.forEach((payment) => {
      if (payment.price_id && !(payment.price_id in campaignStatuses)) {
        checkCampaignStatus(payment.price_id);
      }
    });
  }, [payments, checkCampaignStatus, campaignStatuses]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('ログアウト失敗:', error);
    } finally {
      localStorage.clear();
      navigate('/login');
    }
  };

  const handleCardInfoChange = async (linkage_identifier) => {
    try {
      const response = await getPortalSession(linkage_identifier);

      if (response.url) {
        window.location.href = response.url;
      } else {
        showNotice('カード情報変更ページの取得に失敗しました');
      }
    } catch (error) {
      console.error('カード情報変更エラー:', error.response?.data);
    }
  };

  if (plansLoading || userLoading) return <p>情報を読み込み中...</p>;

  return (
    <UserPageLayout
      title="マイページ"
      maxWidth="sm"
      titleActions={
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
          }}
        >
          ログアウト
        </Button>
      }
    >
      <Box
        component="div"
        sx={{
          maxWidth: 'none',
          minHeight: 'fit-content',
          padding: 0,
          mt: '3dvh',
          alignItems: 'flex-start',
        }}
      >
        <Typography variant="h5">お客様情報</Typography>
        <Box sx={{ py: 2 }}>
          <Stack direction="column" spacing={1}>
            <Typography variant="body1">
              <strong>氏名:</strong> {user?.name}
            </Typography>
            <Typography variant="body1">
              <strong>メールアドレス:</strong> {user?.email}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="column" spacing={1} sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: 1,
            }}
            size="small"
            onClick={() => navigate('/reset-userinformation')}
          >
            お客様情報の変更
          </Button>
          <Button
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: 1,
            }}
            size="small"
            onClick={() => navigate('/reset-password')}
          >
            パスワードの変更
          </Button>
        </Stack>
        <Divider sx={{ mb: 3, borderBottomWidth: 2 }} />
        <Typography variant="h5" sx={{ mb: 2 }}>
          契約情報
        </Typography>
        {paymentsLoading ? (
          <p>支払い情報を読み込み中...</p>
        ) : payments.length > 0 ? (
          <Stack spacing={2}>
            {payments.map((payment, index) => {
              const planName = getPlanName(payment.price_id) || '不明';
              const isCampaignActive =
                campaignStatuses[payment.price_id] !== false;
              const statusColor = !isCampaignActive
                ? 'default'
                : payment.is_member === PURCHASED
                ? 'success'
                : payment.is_member === NOT_PURCHASED
                ? 'default'
                : 'error';
              const isSubscription = getIsSubscription(payment.price_id);

              return (
                <Card key={index} sx={{ p: 2 }}>
                  <CardContent sx={{ p: 0 }}>
                    <Stack spacing={2}>
                      {/* ヘッダー部分 */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        {/* プラン情報 */}
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            契約プラン
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 'medium' }}
                          >
                            {planName}
                          </Typography>
                        </Box>
                        <Chip
                          label={getPaymentMember(
                            payment.is_member,
                            payment.price_id
                          )}
                          color={statusColor}
                          size="small"
                        />
                      </Box>

                      {/* 金額情報 */}
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          金額
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 'medium' }}
                        >
                          {getAmount(payment.price_id)}
                          {getIsSubscription(payment.price_id) ? '円/月' : '円'}
                        </Typography>
                      </Box>

                      {/* 決済番号 */}
                      {payment.linkage_identifier && (
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            決済番号
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily: 'monospace',
                              overflowWrap: 'break-word',
                            }}
                          >
                            {payment.linkage_identifier}
                          </Typography>
                        </Box>
                      )}

                      {/* 日付情報 */}
                      <Stack spacing={1}>
                        {payment.trial_ends_at && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              無料トライアル終了日
                            </Typography>
                            <Typography variant="body1">
                              {formatDateShort(payment.trial_ends_at)}
                            </Typography>
                          </Box>
                        )}

                        {getTrialDays(payment.price_id) > 0 &&
                          payment.is_member === NOT_PURCHASED && (
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                無料トライアル日数
                              </Typography>
                              <Typography variant="body1">
                                {`${getTrialDays(payment.price_id)}日`}
                              </Typography>
                            </Box>
                          )}

                        {payment.next_payment_day &&
                          payment.is_member === PURCHASED && (
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                次回支払日
                              </Typography>
                              <Typography variant="body1">
                                {formatDateShort(payment.next_payment_day)}
                              </Typography>
                            </Box>
                          )}

                        {payment.end_day && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              終了日
                            </Typography>
                            <Typography variant="body1">
                              {formatDateShort(payment.end_day)}
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      {/* 操作ボタン */}
                      <Box sx={{ pt: 1, borderTop: '1px solid #e0e0e0' }}>
                        <Stack direction="column" spacing={1}>
                          {getPaymentButtons(
                            payment.is_member,
                            payment.campaign_code,
                            payment.linkage_identifier,
                            payment.company_id,
                            payment.price_id,
                            planName,
                            isSubscription
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="body1"
                color="text.secondary"
                textAlign="center"
              >
                契約情報がありません
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
      <NoticeModal
        open={noticeOpen}
        message={noticeMsg}
        onClose={() => setNoticeOpen(false)}
      />
      <ConfirmModal
        open={confirmOpen}
        message={confirmMsg}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onConfirmAction();
        }}
      />
    </UserPageLayout>
  );
};

export default MyPage;
